import { ApiError, LegacyApiError } from '@omnipresentgroup/api';
import {
  List,
  Modal,
  type ModalProps,
  Notification,
  notifySuccess,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { expensesPath, useExpensesUpload } from '@omnipresentgroup/domains';
import { Form } from '@omnipresentgroup/flexible-forms';
import type { DocumentUploadRequest } from '@omnipresentgroup/types';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

const MODAL_TITLE = 'Upload expenses';
const EXPENSES_SUCCESS_MESSAGE = 'Expenses uploaded correctly.';
const ERROR_GENERIC_DESCRIPTION =
  'An error occurred while uploading the file. Please contact support.';

const FORM_ID = 'upload-expenses-form';

type Props = {
  modalProps: Omit<ModalProps, 'children'>;
  handleClose: () => void;
};

export const UploadExpensesModal = ({ modalProps, handleClose }: Props) => {
  const queryClient = useQueryClient();

  const [uploadErrorProblems, setUploadErrorProblems] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);

  const { uploadExpensesFile } = useExpensesUpload();

  const uploadExpenses = (fileToUpload: DocumentUploadRequest) =>
    uploadExpensesFile({
      file: fileToUpload.fileToUpload,
    })
      .then(() => {
        setUploadErrorProblems([]);
        notifySuccess({
          title: EXPENSES_SUCCESS_MESSAGE,
        });
        queryClient.invalidateQueries({
          queryKey: [expensesPath],
          refetchType: 'all',
        });

        return {
          fileName: fileToUpload.fileToUpload.name,
        };
      })
      .catch((error: ApiError | LegacyApiError) => {
        if (error instanceof ApiError || error instanceof LegacyApiError) {
          const stringErrors = error.stringErrors || [];
          setUploadErrorProblems(
            stringErrors.map((stringError, index) => ({
              id: String(index),
              name: stringError,
            })),
          );
        } else {
          setUploadErrorProblems([
            {
              id: 'generic-error',
              name: ERROR_GENERIC_DESCRIPTION,
            },
          ]);
        }
      });

  return (
    <Modal {...modalProps} span={6}>
      <Modal.Header title={MODAL_TITLE} onClose={handleClose} />

      <Modal.Body>
        <Stack>
          {uploadErrorProblems.length > 0 && (
            <Notification
              id="expenses-upload-error"
              intent="error"
              title="Upload error"
              description={
                <List
                  showDisc
                  gap="0"
                  items={uploadErrorProblems}
                  keyExtractor="id"
                  render={(item) => (
                    <Typography as="span" size="14">
                      {item.name}
                    </Typography>
                  )}
                />
              }
            />
          )}

          <Form
            id={FORM_ID}
            schema={{
              type: 'object',
              properties: {
                expensesFile: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      id: {
                        type: 'string',
                      },
                      name: {
                        type: 'string',
                      },
                      url: {
                        type: 'string',
                      },
                      signature: {
                        type: 'string',
                      },
                    },
                  },
                },
              },
            }}
            uiSchema={{
              'ui:submitButtonOptions': {
                norender: true,
              },
              expensesFile: {
                'ui:field': 'file-upload',
                'ui:options': {
                  title: '',
                  accept: {
                    'image/*': ['.csv'],
                  },
                  fileInfo: 'You can upload .csv files',
                },
              },
            }}
            formContext={{
              document: {
                uploadDocument: uploadExpenses,
              },
            }}
          />
        </Stack>
      </Modal.Body>
    </Modal>
  );
};
